import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgreementType, UserType } from 'api/types';
import { AnswerNetVideoTPVStatusType, MilestoneType } from 'modules/types';
import { getStatus } from 'modules/status/utils/getStatus';
import { generateRandomString } from 'shared/utils';
import { Accordion, AccordionItemType } from 'shared/components/Accordion/Accordion';
import {
  isWelcomeChecklistFailed,
  isWelcomeChecklistPassed,
  isWelcomeChecklistRequired,
} from 'modules/status/utils/getWelcomeChecklistStatus';
import { AccordionItem } from 'shared/components/Accordion/AccordionItem';
import { AccordionWrapper, AccordionItemWrapper, Status } from './StatusAccordion.styles';
import { AccordionStepIndicator } from './AccordionStepIndicator';
import { ContractSigning } from '../ContractSigning/ContractSigning';
import { ProjectQualification } from '../ProjectQualification/ProjectQualification';
import { SystemInstallation } from '../SystemInstallation/SystemInstallationContent';
import { InService } from '../InService/InService';

export type StatusAccordionItemType = AccordionItemType & {
  status?: Status;
  isOpen: boolean;
};
type Props = {
  currentMilestone: MilestoneType;
  user?: UserType;
  agreement?: AgreementType;
  isFetchingWelcomeChecklist: boolean;
  onOpenWelcomeChecklist: () => void;
  onOpenTpv: () => void;
  displayChangeBillingAddress: boolean;
  onConfirmBillingAddress: () => void;
  onTpvStatusUpdate: (status: AnswerNetVideoTPVStatusType | undefined) => void;
};

export function StatusAccordion({
  currentMilestone,
  user,
  agreement,
  isFetchingWelcomeChecklist,
  onOpenWelcomeChecklist,
  onOpenTpv,
  displayChangeBillingAddress,
  onConfirmBillingAddress,
  onTpvStatusUpdate,
}: Props) {
  const { t } = useTranslation();
  const [tpvStatus, setTpvStatus] = useState<AnswerNetVideoTPVStatusType | undefined>(undefined);

  const handleTpvStatusUpdate = useCallback(
    (status: AnswerNetVideoTPVStatusType | undefined) => {
      setTpvStatus(status);
      onTpvStatusUpdate(status);
    },
    [onTpvStatusUpdate], // Dependencies for useCallback
  );

  const actionNeeded = useMemo(
    () =>
      (agreement?.welcome_checklist_status &&
        !agreement.is_tpv_installer &&
        (isWelcomeChecklistFailed(agreement.welcome_checklist_status) ||
          isWelcomeChecklistRequired(agreement.welcome_checklist_status)) &&
        agreement.current_milestone === MilestoneType.CONTRACT_SIGNED) ||
      (agreement?.is_tpv_installer &&
        (tpvStatus === undefined ||
          tpvStatus === AnswerNetVideoTPVStatusType.PENDING ||
          tpvStatus === AnswerNetVideoTPVStatusType.FAILED) &&
        !isWelcomeChecklistPassed(agreement.welcome_checklist_status) &&
        agreement.current_milestone === MilestoneType.CONTRACT_SIGNED),
    [agreement, tpvStatus],
  );

  const callSupportSubText = useMemo(
    () =>
      (agreement &&
        isWelcomeChecklistFailed(agreement.welcome_checklist_status) &&
        !agreement.is_tpv_installer &&
        agreement.current_milestone === MilestoneType.CONTRACT_SIGNED) ||
      (agreement &&
        agreement.is_tpv_installer &&
        tpvStatus === AnswerNetVideoTPVStatusType.FAILED &&
        agreement.current_milestone === MilestoneType.CONTRACT_SIGNED),
    [agreement, tpvStatus],
  );
  const items = useMemo(() => {
    const milestoneStatus = getStatus(currentMilestone);

    return [
      {
        label: t('contractSigned.contractSigned'),
        content: <ContractSigning />,
        status: milestoneStatus[MilestoneType.CONTRACT_SIGNED],
        id: generateRandomString(),
        isOpen: milestoneStatus[MilestoneType.CONTRACT_SIGNED] === Status.IN_PROGRESS,
      },
      {
        label: t('projectQualification.projectQualification'),
        actionNeeded,
        subText: callSupportSubText ? t('earlyAccess.customerSupport') : '',
        content: (
          <ProjectQualification
            user={user}
            isLoadingWelcomeChecklistQuestions={isFetchingWelcomeChecklist}
            agreement={agreement}
            onOpenWelcomeChecklist={onOpenWelcomeChecklist}
            onOpenTpv={onOpenTpv}
            isCurrentMilestone={
              milestoneStatus[MilestoneType.PROJECT_QUALIFICATION_COMPLETED] === Status.IN_PROGRESS
            }
            onTpvStatusUpdate={handleTpvStatusUpdate}
          />
        ),
        status: milestoneStatus[MilestoneType.PROJECT_QUALIFICATION_COMPLETED],
        id: generateRandomString(),
        isOpen:
          milestoneStatus[MilestoneType.PROJECT_QUALIFICATION_COMPLETED] === Status.IN_PROGRESS,
      },
      {
        label: t('systemInstallation.systemInstallation'),
        content: (
          <SystemInstallation
            displayChangeBillingAddress={displayChangeBillingAddress}
            onConfirmBillingAddress={onConfirmBillingAddress}
          />
        ),
        status: milestoneStatus[MilestoneType.INSTALLATION_COMPLETED],
        id: generateRandomString(),
        isOpen: milestoneStatus[MilestoneType.INSTALLATION_COMPLETED] === Status.IN_PROGRESS,
      },
      {
        label: t('systemOperational.systemOperational'),
        content: <InService />,
        status: milestoneStatus[MilestoneType.IN_SERVICE],
        id: generateRandomString(),
        isOpen:
          milestoneStatus[MilestoneType.IN_SERVICE] === Status.IN_PROGRESS ||
          milestoneStatus[MilestoneType.IN_SERVICE] === Status.COMPLETED,
      },
    ];
  }, [
    agreement,
    user,
    currentMilestone,
    isFetchingWelcomeChecklist,
    onOpenWelcomeChecklist,
    onOpenTpv,
    t,
    displayChangeBillingAddress,
    onConfirmBillingAddress,
    actionNeeded,
    callSupportSubText,
    handleTpvStatusUpdate,
  ]);

  return (
    <AccordionWrapper>
      <Accordion id="status-accordion" items={items}>
        {items.map((item: StatusAccordionItemType, i: number) => (
          <AccordionItemWrapper key={`item-${item.id}`}>
            <AccordionStepIndicator
              step={i + 1}
              showLine={i !== items.length - 1}
              status={item.status}
            />
            <AccordionItem
              id={`item-${item.id}`}
              styleVariant="primary"
              label={item.label}
              subText={item?.subText}
              actionIndicator={item.actionNeeded}
              isOpen={item.isOpen}
              size="large"
            >
              {item.content}
            </AccordionItem>
          </AccordionItemWrapper>
        ))}
      </Accordion>
    </AccordionWrapper>
  );
}

export default StatusAccordion;
